import { SingletonRouter } from "next/router";
import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import posed, { PoseGroup } from "react-pose";
import { useSelector } from "react-redux";

import {
  flightOffersComparator,
  getFilteredFlightOffers,
} from "shared/containers/Filters/helpers";
import { AppState } from "shared/data/reducers";
import { SearchQuery } from "shared/data/reducers/types/storage";
import { addDaysToDate, normalizeDate } from "shared/lib/datetime";
import IFlightOffer from "shared/types/flightOffer";

import FlightOffer from "./FlightOffer";
import FlightSearchResultsBanner from "./FlightSearchResultsBanner";

const Item = posed.div({});

const NUM_SHOW_INITIAL_FLIGHTS = 35;
const NUM_ADD_FLIGHTS_ON_CLICK = 10;

const errorMessageStyle = {
  paddingLeft: 10,
  paddingTop: 30,
  boxSizing: "border-box" as const,
  display: "block",
  background: "white",
  borderRadius: 5,
  padding: 25,
  fontSize: 22,
  lineHeight: 1.6,
  marginTop: 30,
};

interface IListFlights {
  flightOffers: IFlightOffer[];
  url: SingletonRouter;
  searchQuery?: SearchQuery;
  setIsSomeModalOpenned: React.Dispatch<React.SetStateAction<boolean>>;
}

const isDepartureBeforeSearchDays = (
  searchQuery?: SearchQuery,
  blockSearchDaysBefore?: number
): boolean => {
  if (!searchQuery || blockSearchDaysBefore === undefined) {
    return false;
  }
  const today = normalizeDate(new Date());
  const todayDateWithBlackedDays = new Date(
    normalizeDate(addDaysToDate(today.toISOString(), blockSearchDaysBefore))
  );

  return Object.entries(searchQuery).some(([key, value]) => {
    if (key.includes("returnDate") || key.includes("departureDate")) {
      if (value === "") {
        return false;
      }

      const isInvalid =
        todayDateWithBlackedDays > normalizeDate(new Date(value as string));
      return isInvalid;
    }
    return false;
  });
};

const ErrorMessage: React.FC<{ messageId: string }> = ({ messageId }) => {
  const intl = useIntl();
  return (
    <div style={errorMessageStyle}>{intl.formatMessage({ id: messageId })}</div>
  );
};

const ListFlights: React.FC<IListFlights> = ({
  setIsSomeModalOpenned,
  flightOffers,
  url,
  searchQuery,
}) => {
  const intl = useIntl();
  const {
    searchLoading,
    blockSearchDaysBefore,
    isCbt,
    search,
    searchResponseReceived,
  } = useSelector((state: AppState) => ({
    searchLoading: state.loading.searchLoader,
    blockSearchDaysBefore: parseInt(
      state.storage.frontendSettings?.dealerFlightSearchSettings
        ?.blockSearchDaysBefore,
      10
    ),
    isCbt: state.cbt.corporate !== null,
    search: state.search,
    searchResponseReceived: state.search.searchResponseReceived ?? false,
  }));
  const [showNumFlights, setShowNumFlights] = useState(
    NUM_SHOW_INITIAL_FLIGHTS
  );

  const sortFlights = url.query?.sort ? String(url.query.sort) : "PRICE";

  const filteredFlightOffers = useMemo(
    () =>
      getFilteredFlightOffers(
        flightOffers,
        (url.query as unknown) as SearchQuery
      ).sort((a, b) => flightOffersComparator(a, b, sortFlights)),
    [flightOffers, url.query, sortFlights]
  );

  if (search?.error && search?.errorCode) {
    return <ErrorMessage messageId={search.error} />;
  }

  if (
    filteredFlightOffers.length === 0 &&
    !searchLoading &&
    searchResponseReceived
  ) {
    const messageId = isDepartureBeforeSearchDays(
      searchQuery,
      blockSearchDaysBefore
    )
      ? "Flight.notFound.departureBeforeSearchDays"
      : "Flight.notFound";
    return <ErrorMessage messageId={messageId} />;
  }

  return (
    <div className="flights">
      {/* @ts-ignore - PoseGroup has type compatibility issues */}
      <PoseGroup>
        {filteredFlightOffers
          .filter((_, index) => index < showNumFlights)
          .map((flightOffer, index) => (
            <Item key={flightOffer.randomKey}>
              <FlightOffer
                key={JSON.stringify(flightOffer.CurrentCombinations)} // unmount component to reinitialize local state for new combinations
                flightOffer={flightOffer}
                setIsSomeModalOpenned={setIsSomeModalOpenned}
                index={index}
                url={url}
                isCbt={isCbt}
              />
              {index === 0 && filteredFlightOffers.length > 1 && (
                <FlightSearchResultsBanner />
              )}
            </Item>
          ))}
      </PoseGroup>

      {filteredFlightOffers.length > showNumFlights && (
        <div
          className="show-more-flights-wrapper"
          role="button"
          onClick={() =>
            setShowNumFlights(
              (numFlights) => numFlights + NUM_ADD_FLIGHTS_ON_CLICK
            )
          }
        >
          <span>
            {intl.formatMessage({
              id: "Results.showMore",
            })}
          </span>
        </div>
      )}
    </div>
  );
};

export default ListFlights;
